import React, { useMemo } from 'react';

import { SyncIcon, ZapIcon } from '@primer/octicons-react';

import capitalize from 'lodash/capitalize';

import compareDesc from 'date-fns/compareDesc';
import parseISO from 'date-fns/parseISO';

import { formatOrchestrationRunDate, getNextRunFromCronString } from '../../../utils/date_utils';

import AlternatingRowDataTable from '../../common/DataTable/AlternatingRowDataTable';
import Icon from '../../common/icons/Icon';
import Label from '../../common/Labels/Label';

import TaskStatusBadge from '../TaskStatusBadge/TaskStatusBadge';

const transformTaskListData = (taskListRows = []) => taskListRows?.map(taskListItem => ({
  id: taskListItem?.id,
  name: taskListItem?.name,
  runType: taskListItem?.run_type,
  lastRuns: taskListItem?.last_runs,
  schedule: taskListItem?.schedule,
  status: capitalize(taskListItem?.status),
  killed: taskListItem?.killed,
}))

const sortTaskData = (taskRows = []) => taskRows.sort((itemA, itemB) => {
  const firstDate = parseISO(itemA.created_at);
  const secondDate = parseISO(itemB.created_at);

  return compareDesc(firstDate, secondDate);
})

// Oneshot, CronJob, NetworkService
const RunTypeIndicator = ({ runType }) => {
  const chosenIcon = runType === 'Oneshot' ? ZapIcon : SyncIcon;
  return (
    <Icon
      icon={chosenIcon}
      fill="text-brand"
    />
  )
}

const LastRunDateCellRenderer = ({ row, value }) => {
  const lastRun = value[0];
  const nextRunDate = row?.original?.schedule ? getNextRunFromCronString(row?.original?.schedule) : null;

  // TODO - Should this be possible? Should we have a separate value for it?
  if (!lastRun){
    return <Label>--</Label>;
  }
  return (
    <div className="flex flex-col items-start">
      <Label>{formatOrchestrationRunDate(lastRun.created_at)}</Label>
      {
        nextRunDate && (
          <Label>Next Scheduled Run: {formatOrchestrationRunDate(nextRunDate)}</Label>
        )
      }
    </div>
  )
}
const StatusCellRenderer = ({ row }) => {
  const lastRuns = row?.original?.lastRuns;
  const lastRun = lastRuns?.length ? lastRuns[0] : null;
  return <TaskStatusBadge status={lastRun?.status} />
}
const TaskCellRenderer = ({ row, value }) => {
  const name = row?.original?.name;
  const runType = row?.original?.runType;

  return (
    <div className="flex flex-col p-2">
      <div className="flex flex-col flex-wrap items-start mb-2">
        <div className="flex items-center justify-center">
          <RunTypeIndicator runType={runType} />
          <Label
            className="ml-2"
            size="md"
            title={value}
            bold
          >
            {name}
          </Label>
        </div>
        <Label size="md" title={value}>
          {value}
        </Label>
      </div>
    </div>
  )
}

export const TaskListDataTable = ({ taskListData = [] }) => {
  const columns = useMemo(() => [
    {
      Header: 'Task',
      accessor: 'id',
      Cell: TaskCellRenderer,
      width: 160
    },
    {
      Header: 'Last Run Status',
      accessor: 'status', // using last_runs[0]
      Cell: StatusCellRenderer,
      width: 80
    },
    {
      Header: 'Run At',
      accessor: 'lastRuns', // using last_runs[0]
      Cell: LastRunDateCellRenderer,
      width: 80,
      tooltipText: 'Next scheduled time calculated according to CRON settings. Actual run time might differ due to multiple factors.',
    },
  ], []);
  const data = useMemo(() => sortTaskData(transformTaskListData(taskListData)), [taskListData]);
  const defaultColumn = React.useMemo(
    () => ({
      width: 160,
    }),
    []
  )

  return (
    <AlternatingRowDataTable
      id="tasklist-data-table"
      columns={columns}
      data={data}
      defaultColumn={defaultColumn}
    />
  )
};

export default TaskListDataTable;
